import { SalesDailyChecklistProgressTrack } from "./SalesDailyChecklistProgressTrack";
import { SalesDailyChecklistItemRow } from "./SalesDailyChecklistItemRow";
import { formatLocalDayLabel, percentFromFraction } from "./salesDailyChecklistWidgetUtils";
import { useSalesDailyChecklistWidget } from "./useSalesDailyChecklistWidget";
import { WorkplaceRemindersSection } from "@/components/messages/WorkplaceRemindersSection";
import { useAuth } from "@/contexts/AuthContext";
import { useFulfillmentIntakeNag } from "@/components/fulfillment/useFulfillmentIntakeNag";
import { FIREFLY_TARGET_ATTR } from "@/lib/firefly/fireflyTargetConvention";

type Props = {
  scopedOrgId: string | null;
  onOpenAdsAssistant?: () => void;
  onOpenAdsAssistantSuggestion?: () => void;
  /** Open follow-ups due today or overdue (workspace leads); drives collapsed-header pulse + Sales Console link. */
  followUpAttentionCount?: number;
  onOpenSalesConsole?: () => void;
};

/**
 * Weighted daily progress (manual tasks + up to 6 ad logs); collapsed as a % + “line push” bar.
 */
export function SalesDailyChecklistWidget({
  scopedOrgId,
  onOpenAdsAssistant,
  onOpenAdsAssistantSuggestion,
  followUpAttentionCount = 0,
  onOpenSalesConsole,
}: Props) {
  const {
    expanded,
    setExpanded,
    items,
    fraction,
    allDone,
    loading,
    error,
    togglingId,
    pendingConfirm,
    setPendingConfirm,
    orgManagerNudge,
    pendingRequiredAckCount,
    ackBlockingEnabled,
    load,
    onToggle,
  } = useSalesDailyChecklistWidget(scopedOrgId);

  // Slice 1E (Fulfillment): show a passive "Intake forms needing your
  // attention" line when the user is a sales-role user on an org with
  // a sales floor (capability flag from AuthContext, Slice 3 — replaces
  // the original VITE_FULFILLMENT_PILOT env gate).
  const auth = useAuth();
  const intakeNag = useFulfillmentIntakeNag(auth.role === "sales" && auth.hasSalesFloor);

  if (!scopedOrgId) return null;

  const done = fraction?.done ?? 0;
  const total = fraction?.total ?? 0;
  const pct = percentFromFraction(done, total);

  const showFollowUpAlert = !expanded && followUpAttentionCount > 0 && !!onOpenSalesConsole;

  const followUpAlert = showFollowUpAlert ? (
    <button
      type="button"
      className={
        "absolute right-2 top-2 z-10 flex h-9 min-w-9 items-center justify-center rounded-full border-2 border-amber-400 bg-amber-100 px-2 " +
        "text-[11px] font-bold tabular-nums text-amber-950 shadow-[0_0_14px_rgba(251,191,36,0.45)] " +
        "animate-pulse motion-reduce:animate-none motion-reduce:shadow-none " +
        "ring-2 ring-amber-300/70 dark:border-amber-500 dark:bg-amber-950/45 dark:text-amber-100 dark:ring-amber-600/60 " +
        "dark:shadow-[0_0_16px_rgba(251,191,36,0.35)] hover:brightness-[1.03] focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
      }
      aria-label={`Open sales console: ${followUpAttentionCount} open follow-up${followUpAttentionCount === 1 ? "" : "s"} due or overdue`}
      onClick={onOpenSalesConsole}
    >
      {followUpAttentionCount > 9 ? "9+" : followUpAttentionCount}
    </button>
  ) : null;

  return (
    <section
      className="relative mb-6 text-left rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden dark:border-gray-700 dark:bg-zinc-950/40"
      aria-label="Daily sales checklist"
    >
      <button
        type="button"
        {...{ [FIREFLY_TARGET_ATTR]: "sc-daily-checklist" }}
        onClick={() => setExpanded((e) => !e)}
        className={
          "group w-full px-4 py-3 text-left transition-colors hover:bg-gray-50/90 dark:hover:bg-zinc-900/60 " +
          (showFollowUpAlert ? "pr-12 sm:pr-14" : "")
        }
      >
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="flex min-w-0 items-center gap-2">
            <span className="text-lg shrink-0 text-gray-700 dark:text-gray-200" aria-hidden>
              {allDone ? "✓" : "◎"}
            </span>
            <div className="min-w-0">
              <div className="font-semibold text-sm text-gray-900 dark:text-gray-100 sm:text-base">Daily checklist</div>
              <div className="text-[11px] text-gray-500 dark:text-gray-400">{formatLocalDayLabel()}</div>
            </div>
          </div>
          <div className="flex shrink-0 flex-col items-end gap-0.5">
            <span className="tabular-nums text-xl font-bold leading-none text-gray-900 dark:text-white">{pct}%</span>
            <span className="text-[10px] font-medium text-gray-500 dark:text-gray-400 tabular-nums">
              {done}/{total} units
            </span>
            <span className="text-xs text-gray-400 dark:text-gray-500" aria-hidden>
              {expanded ? "▴" : "▾"}
            </span>
          </div>
        </div>
        <SalesDailyChecklistProgressTrack pct={pct} />
        {allDone ? (
          <p className="mt-2 text-center text-xs font-medium text-emerald-700 dark:text-emerald-400">All caught up today</p>
        ) : ackBlockingEnabled && pendingRequiredAckCount > 0 && done >= total && total > 0 ? (
          <p className="mt-2 text-center text-xs font-medium text-amber-800 dark:text-amber-300">
            Tasks done — acknowledge {pendingRequiredAckCount} required message
            {pendingRequiredAckCount === 1 ? "" : "s"} in the header inbox
          </p>
        ) : null}
      </button>

      {followUpAlert}

      {expanded && (
        <div className="border-t border-gray-100 px-4 py-3 bg-gray-50/80 dark:border-gray-700 dark:bg-zinc-900/50">
          <p className="text-xs text-gray-600 dark:text-gray-400 mb-3">
            Progress follows your org template (Ops → Daily checklist): manual tasks, optional confirmation
            steps, and ad logging slots with any platform/keyword rules your admin configured. Manager nudges and
            hygiene reminders are informational unless your org enables acknowledgement blocking for forced workplace
            messages.
          </p>
          <div className="mb-3">
            <WorkplaceRemindersSection
            surface="checklist"
            compact
            orgManagerNudge={orgManagerNudge}
            navigation={{
              onOpenChecklist: () => setExpanded(true),
            }}
            />
          </div>
          {intakeNag.enabled && intakeNag.count != null && intakeNag.count > 0 ? (
            <p className="mb-3 text-[11px] rounded-lg border border-amber-300/80 bg-amber-50/80 px-2.5 py-2 text-amber-950 dark:border-amber-800 dark:bg-amber-950/35 dark:text-amber-100">
              <span className="font-medium">Intake forms needing your attention:</span>{" "}
              <span className="tabular-nums font-semibold">{intakeNag.count}</span> — open the
              Fulfillment → Intake forms mode to finish them.
            </p>
          ) : null}
          {ackBlockingEnabled && pendingRequiredAckCount > 0 ? (
            <p className="mb-3 text-[11px] rounded-lg border border-amber-300/80 bg-amber-50/80 px-2.5 py-2 text-amber-950 dark:border-amber-800 dark:bg-amber-950/35 dark:text-amber-100">
              <span className="font-medium">Completion blocked:</span> acknowledge {pendingRequiredAckCount} required
              workplace message{pendingRequiredAckCount === 1 ? "" : "s"} from the header inbox before closing out your
              day. Personal reminders and org nudges above do not block completion.
            </p>
          ) : null}
          <div className="flex justify-end mb-2">
            <button
              type="button"
              onClick={() => void load()}
              disabled={loading}
              className="rounded-lg border border-gray-200 bg-white px-2.5 py-1 text-xs font-medium text-gray-800 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:bg-zinc-800 dark:text-gray-100 dark:hover:bg-zinc-700"
            >
              {loading ? "…" : "Refresh"}
            </button>
          </div>
          {error ? (
            <p className="text-xs text-amber-900 dark:text-amber-200">{error}</p>
          ) : (
            <ul className="space-y-2">
              {items.map((it) => (
                <SalesDailyChecklistItemRow
                  key={it.id}
                  it={it}
                  loading={loading}
                  togglingId={togglingId}
                  onToggle={onToggle}
                  setPendingConfirm={setPendingConfirm}
                  onOpenAdsAssistant={onOpenAdsAssistant}
                  onOpenAdsAssistantSuggestion={onOpenAdsAssistantSuggestion}
                />
              ))}
            </ul>
          )}
        </div>
      )}

      {pendingConfirm ? (
        // Confirm-completion dialog z-index. The widget can now render
        // inside the `ActivityFloatingDock` (z-9150) and adjacent to rail
        // popovers (z-9200) — bump above both so the confirm modal isn't
        // visually trapped under the dock when the widget is mounted in
        // `ActivityDockDailyChecklistSection`. Outside the dock the bump
        // is a no-op (no nearby competing overlays). See Slice C of
        // docs/planning/activity_dock_workflow_facilitation.md §6.
        <div
          className="fixed inset-0 z-[9300] flex items-center justify-center bg-black/40 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="checklist-confirm-title"
        >
          <div className="w-full max-w-md rounded-2xl border border-gray-200 bg-white p-5 shadow-xl dark:border-gray-600 dark:bg-zinc-900">
            <h2 id="checklist-confirm-title" className="text-base font-semibold text-gray-900 dark:text-gray-100">
              Confirm completion
            </h2>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              Mark <strong>{pendingConfirm.label}</strong> as done for today? This item needs an explicit confirmation.
            </p>
            {pendingConfirm.description ? (
              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">{pendingConfirm.description}</p>
            ) : null}
            <div className="mt-4 flex flex-wrap justify-end gap-2">
              <button
                type="button"
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-100 dark:hover:bg-zinc-800"
                onClick={() => setPendingConfirm(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="rounded-lg bg-blue-700 px-3 py-2 text-sm font-medium text-white hover:bg-blue-800"
                onClick={() => {
                  const p = pendingConfirm;
                  setPendingConfirm(null);
                  if (p) void onToggle(p, true);
                }}
              >
                Yes, mark done
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
}
